import React, { useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { customToast } from '../utils/toastUtils';
import './FeedbackModal.css';

const FeedbackModal = ({ show, onClose }) => {
  const { userData } = useUser();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating === 0) {
      customToast.error('Please provide a rating');
      return;
    }
    
    if (feedback.trim() === '') {
      customToast.error('Please provide your feedback');
      return;
    }

    setIsSubmitting(true);
    try {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:5000';
      const response = await fetch(`${backendUrl}/api/email/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail: userData?.email || 'anonymous@user.com',
          userName: userData?.displayName || userData?.email?.split('@')[0] || 'User',
          rating,
          feedback: feedback.trim()
        })
      });

      const result = await response.json();
      if (result.success) {
        setSubmitted(true);
        customToast.success('Thank you for your feedback!');
        setTimeout(() => {
          setRating(0);
          setFeedback('');
          setSubmitted(false);
          setIsSubmitting(false);
          onClose();
        }, 2000);
      } else {
        throw new Error(result.error || 'Failed to submit feedback');
      }
    } catch (error) {
      console.error('Feedback submission error:', error);
      customToast.error('Failed to submit feedback. Please try again.');
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setRating(0);
    setHoveredRating(0);
    setFeedback('');
    setSubmitted(false);
    onClose();
  };

  const wordCount = feedback.trim().split(/\s+/).filter(word => word.length > 0).length;
  const charCount = feedback.length;

  return (
    <div className="feedback-modal-overlay" onClick={handleClose}>
      <div className="feedback-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="feedback-modal-close" onClick={handleClose}>
          ×
        </button>

        {!submitted ? (
          <>
            <div className="feedback-modal-header">
              <h2>We'd Love Your Feedback!</h2>
              <p>Help us improve ResumeGlobe by sharing your thoughts</p>
            </div>

            <form onSubmit={handleSubmit} className="feedback-form">
              <div className="rating-section">
                <label className="rating-label">How would you rate your experience?</label>
                <div className="star-rating">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      className={`star ${star <= (hoveredRating || rating) ? 'active' : ''}`}
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoveredRating(star)}
                      onMouseLeave={() => setHoveredRating(0)}
                    >
                      ★
                    </button>
                  ))}
                </div>
                {rating > 0 && (
                  <p className="rating-text">
                    {rating === 1 && '😞 Poor'}
                    {rating === 2 && '😕 Fair'}
                    {rating === 3 && '😐 Good'}
                    {rating === 4 && '😊 Very Good'}
                    {rating === 5 && '🤩 Excellent'}
                  </p>
                )}
              </div>

              <div className="feedback-section">
                <label className="feedback-label">Tell us more about your experience</label>
                <textarea
                  className="feedback-textarea"
                  placeholder="Share your thoughts, suggestions, or report any issues..."
                  value={feedback}
                  onChange={(e) => {
                    const words = e.target.value.trim().split(/\s+/).filter(word => word.length > 0);
                    if (words.length <= 500) {
                      setFeedback(e.target.value);
                    }
                  }}
                  rows="8"
                  required
                />
                <div className="feedback-counter">
                  <span className={wordCount > 450 ? 'warning' : ''}>
                    {wordCount}/500 words
                  </span>
                  <span className="char-count">{charCount} characters</span>
                </div>
              </div>

              <button type="submit" className="feedback-submit-btn" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <span className="feedback-spinner"></span>
                    Submitting...
                  </>
                ) : (
                  'Submit Feedback'
                )}
              </button>
            </form>
          </>
        ) : (
          <div className="feedback-success">
            <div className="success-icon">✓</div>
            <h2>Thank You!</h2>
            <p>Your feedback has been submitted successfully.</p>
            <p className="success-subtext">We appreciate you taking the time to help us improve!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackModal;
